import { useState, useEffect } from 'react';

interface IntroScreenProps {
  onEnter: () => void;
}

export default function IntroScreen({ onEnter }: IntroScreenProps) {
  const [visible, setVisible] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleClick = () => {
    setLeaving(true);
    setTimeout(() => {
      setVisible(false);
      onEnter();
    }, 800);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center cursor-pointer select-none"
      style={{
        background: '#F5F0E8',
        opacity: leaving ? 0 : 1,
        transition: leaving ? 'opacity 0.8s ease-in-out' : 'none',
      }}
      onClick={handleClick}
    >
      {/* Corner ornaments */}
      <div className="absolute top-6 left-6 w-16 h-16 border-t border-l border-[#A8323C]/30 pointer-events-none" />
      <div className="absolute top-6 right-6 w-16 h-16 border-t border-r border-[#A8323C]/30 pointer-events-none" />
      <div className="absolute bottom-6 left-6 w-16 h-16 border-b border-l border-[#A8323C]/30 pointer-events-none" />
      <div className="absolute bottom-6 right-6 w-16 h-16 border-b border-r border-[#A8323C]/30 pointer-events-none" />

      {/* Outer decorative frame */}
      <div className="absolute inset-10 border border-[#A8323C]/15 pointer-events-none hidden sm:block" />

      {/* Central content */}
      <div className="relative flex flex-col items-center gap-8 px-8 text-center max-w-2xl mx-auto">

        {/* Top ornamental line */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'none' : 'translateY(-10px)',
            transition: 'opacity 1s ease 0.2s, transform 1s ease 0.2s',
          }}
          className="flex items-center gap-4 w-full justify-center"
        >
          <div className="h-px w-16 sm:w-24 bg-[#A8323C]/35" />
          <span className="text-[#A8323C]/50 text-xs font-serif select-none">✦</span>
          <div className="h-px w-4 bg-[#A8323C]/25" />
          <span className="text-[#A8323C]/30 text-[10px] font-serif select-none">✦</span>
          <div className="h-px w-4 bg-[#A8323C]/25" />
          <span className="text-[#A8323C]/50 text-xs font-serif select-none">✦</span>
          <div className="h-px w-16 sm:w-24 bg-[#A8323C]/35" />
        </div>

        {/* Small label */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transition: 'opacity 1s ease 0.5s',
          }}
        >
          <p className="text-xs tracking-[0.45em] uppercase font-mono text-[#8B6B5A]">
            
          </p>
        </div>

        {/* Main heading */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'none' : 'translateY(20px)',
            transition: 'opacity 1.2s ease 0.7s, transform 1.2s ease 0.7s',
          }}
        >
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-serif italic text-[#2A1A14] leading-tight">
            Вам пришло<br />приглашение
          </h1>
        </div>

        {/* Wax seal image */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'scale(1)' : 'scale(0.7)',
            transition: 'opacity 1s ease 1.1s, transform 1s ease 1.1s',
          }}
          className="my-2"
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#4A1018] border-2 border-[#A8323C]/30 flex items-center justify-center"
            style={{ boxShadow: '0 2px 12px rgba(74,16,24,0.25)' }}>
            <img
              src="/images/pango_intro.jpeg"
              alt="Seal"
              className="w-10 h-10 sm:w-13 sm:h-13 object-contain rounded-full opacity-90"
            />
          </div>
        </div>

        {/* CTA */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transition: 'opacity 1s ease 1.5s',
          }}
          className="space-y-3"
        >
          <div className="inline-flex items-center gap-3 border border-[#A8323C]/50 px-7 py-3 text-[#2A1A14] hover:bg-[#A8323C]/8 transition-colors duration-300"
            style={{ outline: '1px solid rgba(168,50,60,0.15)', outlineOffset: '3px' }}>
            <span className="text-xs tracking-[0.35em] uppercase font-mono text-[#6B1A2A]">Открыть приглашение</span>
            <i className="ri-arrow-right-line text-[#A8323C] text-sm"></i>
          </div>
          <p className="text-[10px] tracking-widest uppercase font-mono text-[#A8907A]">
            нажмите в любом месте
          </p>
        </div>

        {/* Bottom ornamental line */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'none' : 'translateY(10px)',
            transition: 'opacity 1s ease 0.2s, transform 1s ease 0.2s',
          }}
          className="flex items-center gap-4 w-full justify-center"
        >
          <div className="h-px w-16 sm:w-24 bg-[#A8323C]/35" />
          <span className="text-[#A8323C]/50 text-xs font-serif select-none">✦</span>
          <div className="h-px w-4 bg-[#A8323C]/25" />
          <span className="text-[#A8323C]/30 text-[10px] font-serif select-none">✦</span>
          <div className="h-px w-4 bg-[#A8323C]/25" />
          <span className="text-[#A8323C]/50 text-xs font-serif select-none">✦</span>
          <div className="h-px w-16 sm:w-24 bg-[#A8323C]/35" />
        </div>

      </div>
    </div>
  );
}
